/**
 * Public credentialing intake — /vault/onboard/:token
 *
 * Recipient of an admin Nudge lands here (no login required, token-gated).
 * As of iter 128, the top of the page walks them through updating their
 * CAQH profile (add Thrive as a practice location + share CAQH ID) — the
 * full credentialing form below is still required because payer enrollment
 * needs it end-to-end (SSN, DOB, license, address, etc.). Everything is
 * Fernet-encrypted at rest.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ShieldCheck, CheckCircle2, ExternalLink, MessageCircle } from 'lucide-react';
import { ORG_META } from '../lib/org';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Bare axios client (no session cookie), token-gated public endpoint.
// Public page: only the link's ?org decides the company (ignore the admin's saved switcher).
const linkOrg = () => (new URLSearchParams(window.location.search).get('org') === 'ludo' ? 'ludo' : 'thrive');
const publicApi = axios.create({ baseURL: `${API_BASE}/api/vault/nudge`, headers: { 'X-Org': linkOrg() } });

const FIELDS = [
  { key: 'first_name', label: 'First name *', required: true },
  { key: 'last_name', label: 'Last name *', required: true },
  { key: 'credentials', label: 'Credentials (e.g. SLP, CF-SLP)' },
  { key: 'npi', label: 'NPI' },
  { key: 'caqh_id', label: 'CAQH ID' },
  { key: 'license_number', label: 'License number' },
  { key: 'license_state', label: 'License state (2-letter)' },
  { key: 'license_issued', label: 'License issued (YYYY-MM-DD)' },
  { key: 'license_expires', label: 'License expires (YYYY-MM-DD)' },
  { key: 'dob', label: 'Date of birth (YYYY-MM-DD)' },
  { key: 'ssn', label: 'SSN (XXX-XX-XXXX)' },
  { key: 'phone', label: 'Phone' },
  { key: 'gender', label: 'Gender' },
  { key: 'home_address', label: 'Home address' },
  { key: 'suite', label: 'Suite / Apt' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
];

export default function VaultOnboard() {
  const co = ORG_META[linkOrg()];
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    publicApi.get(`/${token}`)
      .then(r => {
        setMeta(r.data);
        setForm(f => ({ ...f, email: r.data.recipient_email || '' }));
      })
      .catch(e => setError(e?.response?.data?.detail || 'This link is not valid.'));
  }, [token]);

  const submit = async () => {
    if (!form.first_name || !form.last_name) {
      toast.error('First & last name are required'); return;
    }
    setBusy(true);
    try {
      await publicApi.post(`/${token}/submit`, form);
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F1EDF5] to-[#E8E5EC] flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center" data-testid="onboard-error">
          <div className="font-serif text-xl text-[#2D2833] mb-3">This onboarding link isn't available</div>
          <div className="text-sm text-[#685B72]">{error}</div>
        </Card>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#F1EDF5] to-[#E8E5EC] flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-10 text-center" data-testid="onboard-done">
          <CheckCircle2 size={44} className="mx-auto text-[#839E97] mb-4" />
          <div className="font-serif text-2xl text-[#2D2833] mb-2">Thanks — you're in.</div>
          <div className="text-sm text-[#685B72] leading-relaxed">Your info is safely encrypted in our vault. Someone from the {co.full} admin team will review it and reach out shortly.</div>
        </Card>
      </div>
    );
  }
  if (!meta) return null;
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F1EDF5] via-white to-[#E8E5EC] px-4 py-10">
      <div className="max-w-2xl mx-auto">
        <Card className="p-8" data-testid="onboard-form">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#685B72] mb-2">
            <ShieldCheck size={14} /> {co.full} · Secure Credentialing
          </div>
          <h1 className="font-serif text-3xl text-[#2D2833]">Welcome — let's get you credentialed.</h1>
          <p className="text-sm text-[#685B72] mt-2 leading-relaxed">
            Hi {meta.recipient_name || 'there'} — to enroll you with our payers we need two things: (1)
            <strong className="text-[#2D2833]"> the credentialing form below</strong> filled in, and (2)
            <strong className="text-[#2D2833]"> your CAQH profile updated</strong> with {co.full} listed as a practice location. Full CAQH steps are at the bottom of this page. Everything is Fernet-encrypted at rest and only visible to {co.full} admin.
          </p>

          {/* Full credentialing form (still required end-to-end for payer enrollment) */}
          <div className="mt-8 border-t border-[#E8E5EC] pt-6">
            <div className="font-serif text-lg text-[#2D2833] mb-1">Step 1 · Credentialing form</div>
            <p className="text-xs text-[#685B72] mb-4 leading-relaxed">
              All fields are required for payer enrollment. Sensitive fields (SSN, DOB, License) are encrypted at rest and only ever decrypted with a per-reveal audit log.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {FIELDS.map(f => (
                <div key={f.key} className={f.key === 'home_address' ? 'md:col-span-2' : ''}>
                  <label className="text-[11px] font-semibold text-[#685B72] uppercase tracking-widest">{f.label}</label>
                  <Input value={form[f.key] || ''} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} data-testid={`onboard-${f.key}`} />
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <Button onClick={submit} disabled={busy} data-testid="onboard-submit" className="w-full h-11 bg-[#685B72] hover:bg-[#685B72] text-white font-semibold">
              {busy ? 'Submitting…' : 'Submit'}
            </Button>
            <div className="text-[11px] text-[#685B72] mt-3 text-center">Link expires {new Date(meta.expires_at).toLocaleDateString()}.</div>
          </div>

          {/* Step 2: CAQH guidance (moved to bottom per user request) */}
          <div className="mt-10 border-t border-[#E8E5EC] pt-6">
            <div className="font-serif text-lg text-[#2D2833] mb-1">Step 2 · Update your CAQH profile</div>
            <p className="text-xs text-[#685B72] mb-4 leading-relaxed">
              Payers pull credentialing details straight from CAQH, so this is the piece that actually unlocks enrollment. Takes about 5 minutes if you already have a profile.
            </p>
          </div>

          {/* Step-by-step: what to do inside CAQH */}
          <div className="mt-2 rounded-xl border border-[#E8E5EC] bg-white p-5" data-testid="onboard-caqh-checklist">
            <div className="font-serif text-lg text-[#2D2833] mb-3">If you already have a CAQH profile</div>
            <ol className="text-sm text-[#2D2833] space-y-2 list-decimal pl-5 leading-relaxed">
              <li>Sign in at <a href="https://proview.caqh.org" target="_blank" rel="noreferrer" className="text-[#685B72] underline font-semibold inline-flex items-center gap-1">proview.caqh.org <ExternalLink size={11} /></a>.</li>
              <li>Open <strong>Personal Information</strong> and confirm your name, DOB, license #, and NPI are current.</li>
              <li>Open <strong>Practice Location</strong> → <strong>Add Location</strong> and enter <strong>{co.full}</strong> (your {co.full} admin can share the exact address + TIN if needed).</li>
              <li>Go to <strong>Attestation</strong> and re-attest so the profile stays "current."</li>
              <li>Copy your <strong>CAQH Provider ID</strong> from the top of the page and paste it into the CAQH ID field above.</li>
            </ol>
          </div>

          {/* Don't have a CAQH profile? */}
          <div className="mt-4 rounded-xl border border-[#D8D6E2] bg-[#F1EDF5]/60 p-5" data-testid="onboard-caqh-newuser">
            <div className="font-serif text-lg text-[#2D2833] mb-2">Don't have a CAQH profile yet?</div>
            <p className="text-sm text-[#2D2833] leading-relaxed">
              No problem — every payer we bill needs one, so it's a one-time setup.
            </p>
            <ol className="text-sm text-[#2D2833] space-y-2 list-decimal pl-5 mt-3 leading-relaxed">
              <li>Go to <a href="https://proview.caqh.org/pr/Registration" target="_blank" rel="noreferrer" className="text-[#685B72] underline font-semibold inline-flex items-center gap-1">proview.caqh.org/pr/Registration <ExternalLink size={11} /></a> and pick <strong>Register Now</strong>.</li>
              <li>Have these ready before you start: NPI, license #, license state + expiration, DOB, SSN, malpractice insurance certificate (COI), work + education history, and 3 professional references.</li>
              <li>CAQH will email you a <strong>Provider ID</strong> as soon as your registration is created — that's the number we need in the form above.</li>
              <li>Finish the ProView profile (all sections must reach 100%) and attest so payers can view it.</li>
              <li>Come back here anytime to complete or update the credentialing form.</li>
            </ol>
            <p className="text-xs text-[#685B72] mt-3">Registration is free for providers. Payers cover the CAQH fee, not you.</p>
          </div>

          {/* Contact / help */}
          <div className="mt-6 rounded-xl border border-[#E8E5EC] bg-white p-5 flex items-start gap-3" data-testid="onboard-help-contact">
            <MessageCircle size={18} className="text-[#685B72] mt-0.5 flex-shrink-0" />
            <div className="text-sm text-[#2D2833] leading-relaxed">
              <div className="font-semibold mb-1">Questions?</div>
              Reach out to <strong>Varnoor Sekhon</strong> anytime — via <strong>Google Chat</strong> or email at{' '}
              <a href="mailto:v.sekhon@thrivespeech.net" className="text-[#685B72] underline font-semibold">v.sekhon@thrivespeech.net</a>. She'll get you unstuck.
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
